<template>
  <section class="questions-page">
    <PageTitle
      :plain="true"
      :close-button="true"
      @close="onClose"
      :last-button="true"
      @last="onLast"
    >{{ $domUtils.decode(afterSurvey.title) }}</PageTitle>

    <SwipeTab
      :items="tabItems"
      v-model="activeKey"
      @change="onTabChange"
      :relaxed="true"
      :show-active="true"
    />

    <div class="questions-container">
      <div class="swiper-container" ref="container">
        <div class="swiper-wrapper">
          <div
            class="swiper-slide"
            v-for="(question, index) in questions"
            :key="question.question_id"
            :data-url="`q-${index + 1}`"
          >
            <Question :order="index + 1" :item="question" @saved="onSaved" @error="onError" />
          </div>
          <div class="swiper-slide" v-if="questions.length > 0" data-url="last">
            <AfterSurveyQuestionLast :afterSurveyId="this.afterSurveyId" @send="onSend" />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Swiper from 'swiper'
import api from '@/api/after-survey'
import Question from '@/components/AfterSurveyQuestion.vue'
import AfterSurveyQuestionLast from '@/components/AfterSurveyQuestionLast.vue'
import SwipeTab from '@/components/SwipeTab.vue'
import { renderError } from '@/utils/error'
import { RESPONSE_UNAUTHORIZED } from '@/utils/error'
import QuestionClass from '@/utils/Question'

export default {
  components: {
    Question,
    AfterSurveyQuestionLast,
    SwipeTab
  },

  navbar: false,

  metaInfo() {
    return {
      title: this.afterSurvey.title,
      bodyAttrs: { class: 'is-primary is-footerless after-survey-show' }
    }
  },

  data() {
    return {
      afterSurvey: {
        title: ''
      },
      questions: [],
      swiper: null,
      fromPath: null,
      tabItems: [],
      activeKey: 0,
      hasError: false,
      isEditable: true,
    }
  },

  computed: {
    businessId() {
      return this.$store.getters['auth/businessId']
    },
    afterSurveyId() {
      return this.$route.params.afterSurveyId * 1
    },
    activeSlide() {
      return this.swiper.slides[this.swiper.activeIndex]
    },
    pageHash() {
      return this.activeSlide.dataset.url
    },
    isAnswersUpdated() {
      return this.questions.some(q => q.isAnswerUpdated())
    },
  },

  methods: {
    onTabChange(tabIndex) {
      this.swiper.slideTo(tabIndex)
    },
    onSaved() {
      this.updateTabItems()
      // 間髪入れず次の質問に移ると保存が完了したのかよく分からないので、一瞬待つ
      setTimeout(() => {
        window.scrollTo(0, 0)
        this.swiper.slideNext(500)
      }, 400)
    },
    onError(error) {
      this.hasError = true
      renderError(error)
    },
    createSwiper() {
      return new Swiper(this.$refs.container, {
        spaceBetween: 10,
        centeredSlides: true,
        slidesPerView: 'auto',
        grabCursor: true,
        breakpoints: {
          840: {
            spaceBetween: 20,
            // ボタンクリックなどしようとしてスライドする誤操作を防ぐ
            shortSwipes: false,
          }
        }
      })
    },
    initSwiper() {
      this.swiper = this.createSwiper()

      this.onInitSwiper()
      this.swiper.on('slideChangeTransitionEnd', this.updatePageHash)
      this.swiper.on('slideNextTransitionStart', this.onNextStart)
      this.swiper.on('slideNextTransitionEnd', this.onNextEnd)
    },
    /**
     * URLハッシュから対象の質問まで自動スクロール
     */
    slideToUrlHash() {
      const hash = document.location.hash.replace('#', '')
      if (!hash) {
        return false
      }

      const index = this.hashToIndex(hash)
      if (index === false) {
        return false
      }

      // updatePageHashを発動させたくないので、第三引数にfasleを指定する
      // -> updatePageHashを呼ぶslideChangeTransitionEndが発火しない
      // この関数が呼ばれるのは主にブラウザバック時なので、履歴を更新しないため
      this.swiper.slideTo(index, 200, false)

      // タブ移動
      this.activeKey = index
    },
    hashToIndex(hash) {
      const slide = document.querySelector(`[data-url="${hash}"]`)
      if (!slide) {
        return false
      }

      const siblings = slide.parentNode.children
      return Array.prototype.slice.call(siblings).indexOf(slide)
    },
    updatePageHash() {
      history.pushState(null, '', `${this.$route.path}#${this.pageHash}`)

      // タブ更新
      const index = this.hashToIndex(this.pageHash)
      if (index !== false) {
        this.activeKey = index
      }
    },
    onInitSwiper() {
      if (document.location.hash) {
        // URLハッシュがあれば対象の質問までスライド
        this.slideToUrlHash()
      } else {
        // 初期表示なので履歴の追加（push）ではなく置き換え（replace）
        history.replaceState(null, '', `${this.$route.path}#${this.pageHash}`)
      }

      // ブラウザバック時にURLハッシュを書き換える
      window.addEventListener('popstate', this.slideToUrlHash)
    },
    getSlidedId() {
      const index = this.swiper.previousIndex
      return this.questions[index].question_id
    },
    onNextStart() {
      this.slidings = Object.assign({}, this.slidings, {
        [this.getSlidedId()]: true
      })
    },
    onNextEnd() {
      this.slidings = Object.assign({}, this.slidings, {
        [this.getSlidedId()]: false
      })
    },
    onClose() {
      if (this.fromPath) {
        // 遷移元に戻る
        this.$router.push(this.fromPath)
      } else {
        // 遷移元なしの場合は施行後アンケート一覧画面へ
        this.$wcRouter.push({ name: 'after-survey.index' })
      }
    },
    onLast() {
      // 最終ページに遷移する（質問数+1が最終ページになる）
      this.onTabChange(this.questions.length + 1)
    },
    onSend() {
      // 送信したら、onCloseと同じ処理をする
      this.onClose()
    },
    /**
     * 質問グループ取得
     */
    async fetchAfterSurvey() {
      try {
        const afterSurveys = await api.afterSurveys(this.businessId)
        for (const afterSurvey of afterSurveys) {
          if (afterSurvey.after_survey_id === this.afterSurveyId) {
            this.afterSurvey.title = afterSurvey.after_survey_title
            return false
          }
        }
        if (this.afterSurvey.title === '') {
          // 編集可能日数を過ぎた施行後アンケートのURL直叩き防止
          this.isEditable = false;
        }
      } catch(err) {
        renderError(err)
      }
    },
    /**
     * 質問の一覧を取得
     */
    async fetchQuestions() {
      try {
        const response = await api.questions(this.businessId, this.afterSurveyId)

        this.questions = response.map(res => new QuestionClass(res))

        this.updateTabItems()

        this.$nextTick(() => this.initSwiper())
      } catch(err) {
        renderError(err)
      }
    },
    updateTabItems() {
      this.tabItems = [
        ...this.questions.map((q, index) => ({
          key: index,
          label: `Q${index + 1}`,
          muted: q.answered
        })),
        { key: this.questions.length, label: '完了', muted: false }
      ]
    },
    /**
     * 質問が編集可能かどうかの確認
     */
    checkEditable() {
      if (!this.isEditable) {
        renderError({ response : { status : RESPONSE_UNAUTHORIZED}})
        return
      }
    },
  },

  created() {
    this.$withLoading(async () => {
      await Promise.all([
        this.fetchAfterSurvey(),
        this.fetchQuestions(),
      ])

      this.checkEditable()
    })
  },

  beforeDestroy() {
    window.removeEventListener('popstate', this.slideToUrlHash)
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      // 遷移元なしのパターン
      // 画面リロードや直接遷移など
      if (from.fullPath === '/') {
        return false
      }

      vm.fromPath = from.fullPath
    })
  },

  beforeRouteLeave(to, from, next) {
    if (this.isAnswersUpdated && !this.hasError) {
      // 未保存の更新あり
      const result = window.confirm(`まだ保存されていない回答があります。この画面から離れますか？`)

      return next(result)
    }

    next()
  }
}
</script>
